<template>
  <div class="flex flex-col w-full h-full">
    <!-- Header -->
    <div class="mb-4">
      <h2 class="text-xl font-semibold">
        Notes
      </h2>
      <p class="text-sm text-gray-600 mt-1">
        View and edit notes for "{{ itemName }}"
      </p>
    </div>

    <!-- Notes Forms -->
    <div class="flex-1 space-y-6 overflow-y-auto">
      <!-- Internal Notes -->
      <div class="field">
        <label
          for="internalNotes"
          class="block text-sm font-medium mb-2"
        >
          <i class="pi pi-building mr-2" />
          Internal Notes
          <span class="text-xs text-gray-500 ml-1">(Internal use only)</span>
        </label>
        <Textarea 
          id="internalNotes"
          v-model="notesData.warehouse_notes" 
          class="w-full"
          :rows="3"
          placeholder="Internal notes for team members, setup instructions, technical specifications..."
        />
      </div>

      <!-- External Notes -->
      <div class="field">
        <label
          for="externalNotes"
          class="block text-sm font-medium mb-2"
        >
          <i class="pi pi-file-export mr-2" />
          External Notes
          <span class="text-xs text-gray-500 ml-1">(Visible on quotes)</span>
        </label>
        <Textarea 
          id="externalNotes"
          v-model="notesData.quote_notes" 
          class="w-full"
          :rows="3"
          placeholder="Client-facing information, delivery instructions, special requests..."
        />
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end gap-2 mt-6 pt-4 border-t">
      <Button 
        label="Cancel" 
        icon="pi pi-times"
        severity="secondary"
        @click="closeModal"
      />
      <Button 
        label="Save Notes" 
        icon="pi pi-check"
        :loading="isSubmitting"
        @click="saveNotes"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps, defineEmits } from 'vue';
import { useToastService } from '@/composables/useToastService';
import { projectStore } from '../projectStore';

const props = defineProps({
    itemId: { type: [Number, String], required: true },
    parentItemId: { type: [Number, String], default: null },
    groupId: { type: [Number, String], default: null },
    itemName: { type: String, required: true },
    currentNotes: { 
        type: Object, 
        default: () => ({
            warehouse_notes: '',
            quote_notes: ''
        })
    },
    userRole: { type: String, default: 'user' }
});

const emit = defineEmits(['close', 'notes-updated']);

const toast = useToastService();

const notesData = ref({
    warehouse_notes: '',
    quote_notes: ''
});

const originalNotes = ref({
    warehouse_notes: '',
    quote_notes: ''
});

const isSubmitting = ref(false);

// Get the correct item ID to use for API calls
const contentId = computed(() => {
    // Use itemId if provided, otherwise fall back to parentItemId or groupId
    return props.itemId || props.parentItemId || props.groupId;
});



const saveNotes = async () => {
    isSubmitting.value = true;
    
    try {
        const projectId = projectStore.currentProjectId;
        const equipmentListId = projectStore.currentEquipmentListId;
        
        // Prepare update data
        const updateData = {
            warehouse_notes: notesData.value.warehouse_notes || null,
            quote_notes: notesData.value.quote_notes || null
        };
        
        const { updateEquipmentListContent } = await import('@/services/projectService');
        const response = await updateEquipmentListContent(
            projectId,
            equipmentListId,
            contentId.value,
            updateData
        );
        
        if (response.success) {
            // Update original notes to reflect saved state
            originalNotes.value = { ...notesData.value };
            
            // Emit success event
            emit('notes-updated', {
                itemId: contentId.value,
                notes: notesData.value
            });
            
            // Patch item in place - no full refresh
            if (projectStore.updateContentItemInPlace && typeof projectStore.updateContentItemInPlace === 'function') {
                projectStore.updateContentItemInPlace(contentId.value, {
                    warehouse_notes: notesData.value.warehouse_notes,
                    quote_notes: notesData.value.quote_notes
                });
            }
            
            toast.add({
                severity: 'success',
                summary: 'Notes Saved',
                detail: 'Equipment notes have been saved successfully',
                life: 3000
            });
            
            closeModal();
        } else {
            throw new Error(response.message || 'Failed to update notes');
        }
    } catch (error) {
        toast.add({
            severity: 'error',
            summary: 'Error',
            detail: `Failed to update notes: ${error.message}`,
            life: 3000
        });
    } finally {
        isSubmitting.value = false;
    }
};


const closeModal = () => {
    emit('close');
};

// Initialize notes data
onMounted(() => {
    notesData.value = {
        warehouse_notes: props.currentNotes.warehouse_notes || '',
        quote_notes: props.currentNotes.quote_notes || ''
    };
    
    originalNotes.value = { ...notesData.value };
});
</script>

<style scoped>
.field {
    @apply mb-4;
}

.field label {
    @apply text-gray-700 font-medium;
}

.field small {
    @apply text-gray-500 text-xs mt-1 block;
}

.notes-section {
    @apply space-y-4;
}

/* Custom styling for textarea */
:deep(.p-inputtextarea) {
    @apply border-gray-300 rounded-md;
}

:deep(.p-inputtextarea:focus) {
    @apply border-blue-500 ring-1 ring-blue-500;
}
</style>
